import { TUTORIAL_CONNECTION_ID, TUTORIAL_ROUTE_ID } from "./tutorialData";

// GUNDI-5665 tutorial step definitions. Order IS the tour. Each step names the
// page it runs on (the provider navigates when path changes between steps), the
// element it spotlights, and the translation key of its message.
//
// `advanceAction` runs when the user clicks Next ON that step, BEFORE the tour
// moves to the next one — it drives the real UI on the user's behalf (open the
// destination drawer, tick the candidate, attach it, open the node's panel).
// The user never clicks the app directly; the overlay swallows those clicks.
//
// TEMPORARY (≤3 weeks) — see the removal manifest in tutorialInterceptor.ts.
export type TutorialStepDef = {
    id: string;
    path: string;
    targetTestId: string;
    messageKey: string;
    advanceAction?: () => void;
    /** Optional per-step card position; the overlay's default slot otherwise. */
    slot?: "upper-right";
};

const click = (testId: string) => () => {
    document.querySelector<HTMLElement>(`[data-testid="${testId}"]`)?.click();
};

const ROUTE_PATH = `/routes/${TUTORIAL_ROUTE_ID}`;
// Verified against the live page: the Connection detail page has exactly TWO
// tabs — "Configuration" (slug `configuration`, at the bare /connections/:id)
// and "Routes" (slug `details`, at /connections/:id/routes). Their testids were
// added for this tour in DestinationDetailsModal.tsx.
//
// The view-connection step's simulated click lands on /configuration (that is
// where the flow map's own link goes), so the next step declares that same
// path and the provider's navigate is a no-op on top of it.
const CONNECTION_PATH = `/connections/${TUTORIAL_CONNECTION_ID}`;

// Nav steps spotlight the label span (nav_*_label), not the NavLink itself:
// the active item's underline padding inflates the link's box, which made the
// Routes cutout visibly larger than the Connections one.
export const TUTORIAL_STEPS: TutorialStepDef[] = [
    { id: "routes-what", path: "/", targetTestId: "nav_routes_label", messageKey: "tutorial.routes_what" },
    { id: "routes-rename", path: "/", targetTestId: "nav_routes_label", messageKey: "tutorial.routes_rename" },
    { id: "connections-what", path: "/", targetTestId: "nav_connections_label", messageKey: "tutorial.connections_what" },
    { id: "connections-rename", path: "/", targetTestId: "nav_connections_label", messageKey: "tutorial.connections_rename" },
    { id: "sources-what", path: "/", targetTestId: "nav_sources_label", messageKey: "tutorial.sources_what" },
    { id: "workspaces-what", path: "/", targetTestId: "nav_workspaces_label", messageKey: "tutorial.workspaces_what" },
    // The guided journey: a Route with no destination yet → open the picker →
    // select the tour's EarthRanger → attach it → open its node → jump to its
    // Connection page. Each Next performs the click it just described.
    { id: "flowmap-what", path: ROUTE_PATH, targetTestId: "RouteFlowMap-container", messageKey: "tutorial.flowmap_what" },
    {
        id: "flowmap-add-dest", path: ROUTE_PATH, targetTestId: "RouteFlowMap-add-destination-button",
        messageKey: "tutorial.flowmap_add_dest", advanceAction: click("RouteFlowMap-add-destination-button"),
    },
    {
        // The candidate ROW, not the whole panel: spotlighting the full-height
        // drawer read as no spotlight at all.
        id: "drawer-pick", path: ROUTE_PATH, targetTestId: "AddDestinationDrawer-candidate-row",
        messageKey: "tutorial.drawer_pick", advanceAction: click("AddDestinationDrawer-candidate-checkbox"),
    },
    {
        id: "drawer-attach", path: ROUTE_PATH, targetTestId: "AddDestinationDrawer-attach-button",
        messageKey: "tutorial.drawer_attach", advanceAction: click("AddDestinationDrawer-attach-button"),
    },
    {
        id: "flowmap-added", path: ROUTE_PATH, targetTestId: "AccountNode-destination-div",
        messageKey: "tutorial.flowmap_added", advanceAction: click("AccountNode-destination-div"),
    },
    {
        id: "view-connection", path: ROUTE_PATH, targetTestId: "ConfigurationsTab-view-connection-button",
        messageKey: "tutorial.view_connection", advanceAction: click("ConfigurationsTab-view-connection-button"),
    },
    // upper-right slot (product call): in the default slot the card sat over
    // the connection page's configuration form while describing its tab.
    { id: "conn-tab-config", path: `${CONNECTION_PATH}/configuration`, targetTestId: "ConnectionDetails-configuration-tab", messageKey: "tutorial.conn_tab_config", slot: "upper-right" },
    { id: "conn-tab-routes", path: `${CONNECTION_PATH}/routes`, targetTestId: "ConnectionDetails-details-tab", messageKey: "tutorial.conn_tab_routes", slot: "upper-right" },
];
